import store from "../store";

const hasRole = (roleName) => {
    let roles = JSON.parse(localStorage.getItem("roles") ?? "[]");
    return roles.indexOf(roleName) !== -1;
};

const formatter = (
    number,
    language = "en",
    currency = "EUR",
    simpleNumber,
    minimumFractionDigits = 2,
    maximumFractionDigits = 20
) => {
    const languages = {
        en: "GB",
        de: "DE",
        fr: "FR",
        es: "ES",
        tu: "TU",
        ch: "CH",
        ar: "AR",
        ja: "JA"
    };
    let formattedNumber = number;
    try {
        let obj = {};
        if (!simpleNumber) {
            obj = {
                style: "currency",
                currency: currency,
            };
        }
        formattedNumber = new Intl.NumberFormat(
            language + "-" + languages[language],
            {
                ...obj,
                minimumFractionDigits: minimumFractionDigits,
                maximumFractionDigits: maximumFractionDigits,
            }
        ).format(number);
    } catch (e) {
        console.log(e);
    } finally {
        return formattedNumber;
    }
};

const getDateValue = (date) => {
    const dateTimePattern = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
    if (
        typeof date === "string" &&
        dateTimePattern.test(date) &&
        !/([zZ]|[+-]\d{2}:?\d{2})$/.test(date)
    ) {
        return `${date.replace(" ", "T")}Z`;
    }
    return date;
};

const dateFormatter = (date, language = "en") => {
    let formattedDate = "";
    try {
        const dateTemp = new Date(date);
        const separators = {
            en: "/",
            de: ".",
            fr: "/",
            es: "/",
            tu: ".",
        };
        const pad = (value) => (value < 10 ? `0${value}` : value);
        const formatDate = (dateTemp, separator) => {
            const day = pad(dateTemp.getDate());
            const month = pad(dateTemp.getMonth() + 1);
            const year = dateTemp.getFullYear();
            const hours = pad(dateTemp.getHours());
            const minutes = pad(dateTemp.getMinutes());
            const dateString = `${day}${separator}${month}${separator}${year}`;
            const timeString = `${hours}:${minutes}`;
            return date.includes(":")
                ? `${dateString} ${timeString}`
                : dateString;
        };

        if (date && !isNaN(dateTemp)) {
            formattedDate = formatDate(dateTemp, separators[language] || "/");
        }
    } catch (e) {
        console.error(e);
    } finally {
        return formattedDate;
    }
};

const timeAgo = (date)  => {
    const now = new Date();
    const createdAt = new Date(date);
    const seconds = Math.floor((now - createdAt) / 1000);

    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) {
        return interval + " years ago";
    }
    interval = Math.floor(seconds / 2592000);
    if (interval > 1) {
        return interval + " months ago";
    }
    interval = Math.floor(seconds / 86400);
    if (interval > 1) {
        return interval + " days ago";
    }
    interval = Math.floor(seconds / 3600);
    if (interval > 1) {
        return interval + " hours ago";
    }
    interval = Math.floor(seconds / 60);
    if (interval > 1) {
        return interval + " minutes ago";
    }
    return "just now";
};

const getMonthDiff = (start, end) => {
    let months =
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth());
    if (end.getDate() < start.getDate()) {
        months -= 1;
    }
    return months < 0 ? 0 : months;
};

const createdBucket = (dateValue) => {
    if (!dateValue) {
        return "--";
    }
    const createdDate = new Date(dateValue);
    if (Number.isNaN(createdDate.getTime())) {
        return "--";
    }
    const now = new Date();
    let diff = now.getTime() - createdDate.getTime();
    if (diff < 0) {
        diff = 0;
    }
    const minute = 60000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    if (diff < minute) return "< 1 min";
    if (diff < 5 * minute) return "< 5 min";
    if (diff < 15 * minute) return "< 15 min";
    if (diff < 30 * minute) return "< 30 min";
    if (diff < hour) return "< 1h";
    if (diff < 2 * hour) return "< 2h";
    if (diff < 4 * hour) return "< 4h";
    if (diff < 6 * hour) return "< 6h";
    if (diff < 8 * hour) return "< 8h";
    if (diff < 12 * hour) return "< 12h";
    if (diff < day) return "< 1d";
    if (diff < 2 * day) return "< 2d";
    if (diff < week) return "< 1w";
    if (diff < 2 * week) return "< 2w";
    const monthsDiff = getMonthDiff(createdDate, now);
    if (monthsDiff < 12) {
        const monthBucket = Math.max(monthsDiff + 1, 1);
        return `< ${monthBucket} Months`;
    }
    const years = Math.ceil(diff / (365 * day));
    return years <= 1 ? "< 1 yr" : `< ${years} yrs`;
};

const adjustForDST = (date) => {
    // Get the current time zone offset in minutes
    var standardOffset = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();

    // Check if daylight saving time is in effect
    var isDST = date.getTimezoneOffset() < standardOffset;

    // Adjust the date for daylight saving time
    var adjustedDate = new Date(
        date.getTime() + (isDST ? -1 : 1) * 60 * 60 * 1000
    );

    return adjustedDate;
};

/**
 * converts dateTime into local browser dateTime
 * @param {date} date to be converted
 * @returns converted dateTime in local browser time
 */
const localTime = (date) => {
    // Get the current time zone offset in minutes
    const utcDate = new Date(date);
    const offset = utcDate.getTimezoneOffset() * 60000; // Offset in milliseconds
    const localTimestamp = utcDate.getTime() - offset;
    const localDate = new Date(localTimestamp);
    return localDate.toLocaleString();
};

/**
 * converts time into local browser time
 * @param {date} date to be converted
 * @returns converted time in local browser time
 */
const onlyLocalTime = (time) => {
    const utcTime = new Date(`2022-01-01T${time}Z`);
    const hours = utcTime.getHours();
    const minutes = utcTime.getMinutes();
    const seconds = utcTime.getSeconds();

    // Formatting hours, minutes, and seconds to ensure leading zeros if necessary
    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

    // Concatenating hours, minutes, and seconds
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

/**
 * converts dashed string to capitalizes string without dashes
 * @param {string} string to be converted
 * @returns capitalized string without dashes
 */
const dashedToRegularFormat = (string) => {
    try {
        // Split the input string by dashes
        const words = string.split("-");

        // Capitalize each word and join them with spaces
        const capitalizedWords = words.map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        );

        // Join the capitalized words to form the final string
        const result = capitalizedWords.join(" ");

        return result;
    } catch (e) {
        return string;
    }
};

const scrollOnErrorField = () => {
    const firstErrorField = document.querySelector(".error");
    if (firstErrorField) {
        firstErrorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    }
}

export default {
  dashedToRegularFormat: dashedToRegularFormat,
  dateFormatter: dateFormatter,
  timeAgo: timeAgo,
  createdBucket: createdBucket,
  formatter: formatter,
  localTime: localTime,
  onlyLocalTime: onlyLocalTime,
  hasRole: hasRole,
  scrollOnErrorField: scrollOnErrorField,
  can: (permissionName) => {
    let permissions = store.getters["auth/userPermissions"];
    return permissions.indexOf(permissionName) !== -1 || hasRole("admin");
  },
  can2: (permissionName) => {
    let permissions = store.getters["auth/userPermissions"];
    return permissions.indexOf(permissionName) !== -1;
  },
};
